import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { getAccessToken } from "../../api/getAccessToken";
import type {
  EarlyPayoutRuleLinePayload,
  EarlyPayoutRuleType,
  EarlyPayoutRuleset,
  EarlyPayoutSelector,
  EarlyPayoutSelectorStatus,
  EarlyPayoutSelectOption,
} from "../../components/Sportsbook/EarlyPayout/earlyPayoutTypes";
import {
  formatEarlyPayoutMarketLabel,
  getEarlyPayoutEventsQueryDateRange,
  mapRuleLinesApiToSoccerTotalConditions,
  normalizeEarlyPayoutRuleType,
} from "../../components/Sportsbook/EarlyPayout/earlyPayoutUtils";

export const EARLY_PAYOUT_RULES_QUERY_KEY = "EARLY_PAYOUT_RULES" as const;
export const EARLY_PAYOUT_SPORTS_DROPDOWN_QUERY_KEY =
  "EARLY_PAYOUT_SPORTS_DROPDOWN" as const;
export const EARLY_PAYOUT_MARKETS_DROPDOWN_QUERY_KEY =
  "EARLY_PAYOUT_MARKETS_DROPDOWN" as const;
export const EARLY_PAYOUT_SELECTORS_QUERY_KEY = "EARLY_PAYOUT_SELECTORS" as const;
export const EARLY_PAYOUT_LEAGUES_DROPDOWN_QUERY_KEY =
  "EARLY_PAYOUT_LEAGUES_DROPDOWN" as const;
export const EARLY_PAYOUT_EVENTS_DROPDOWN_QUERY_KEY =
  "EARLY_PAYOUT_EVENTS_DROPDOWN" as const;

export type EarlyPayoutRuleApi = {
  rule_id: string;
  advantage: number;
  market_id: string;
  market_name: string;
  market_display_name?: string;
  rule_name: string;
  sport_id: string;
  sport_name: string;
  rule_type?: string;
  lines?: EarlyPayoutRuleLinePayload[];
  created_at: string;
  updated_at: string;
};

type EarlyPayoutRulesApiResponse = {
  success?: boolean;
  message?: string;
  data?: EarlyPayoutRuleApi[];
};

export type EarlyPayoutSportDropdownItem = {
  sport_id: string;
  title: string;
};

export type EarlyPayoutMarketDropdownItem = {
  market_id: string;
  title: string;
};

export type EarlyPayoutLeagueDropdownItem = {
  tournament_id: string;
  title: string;
  tournament_name: string;
  category_id: string | null;
  category_name: string | null;
};

export type EarlyPayoutEventDropdownItem = {
  event_id: string;
  title: string;
  event_name: string;
  sport_id: string;
  sport_name: string;
  tournament_id: string;
  tournament_name: string;
  category_id: string | null;
  category_name: string | null;
  home_name: string;
  away_name: string;
};

type EarlyPayoutDropdownApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T[];
};

export type CreateEarlyPayoutRulePayload = {
  rule_name: string;
  rule_type: EarlyPayoutRuleType;
  sport_id: string;
  sport_name: string;
  market_id: string;
  market_name: string;
  advantage: number;
  lines?: EarlyPayoutRuleLinePayload[];
};

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function extractEarlyPayoutApiMessage(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "";
  }

  const message = (data as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

function toEarlyPayoutSelectorPathId(selectorId: string): string {
  return selectorId.replace(/#/g, "%23");
}

export function mapEarlyPayoutRuleApiToRuleset(
  rule: EarlyPayoutRuleApi,
): EarlyPayoutRuleset {
  return {
    id: String(rule.rule_id ?? ""),
    ruleName: String(rule.rule_name ?? ""),
    ruleType: normalizeEarlyPayoutRuleType(rule.rule_type),
    sport: String(rule.sport_name ?? ""),
    market: String(rule.market_name ?? ""),
    ...(typeof rule.market_display_name === "string" &&
    rule.market_display_name.trim()
      ? { marketDisplayName: rule.market_display_name.trim() }
      : {}),
    advantage: finiteNumber(rule.advantage),
    sportId: String(rule.sport_id ?? ""),
    marketId: String(rule.market_id ?? ""),
    createdAt: String(rule.created_at ?? ""),
    updatedAt: String(rule.updated_at ?? ""),
    ...(Array.isArray(rule.lines) && rule.lines.length > 0
      ? { lines: mapRuleLinesApiToSoccerTotalConditions(rule.lines) }
      : {}),
  };
}

export function normalizeEarlyPayoutRulesResponse(
  body: unknown,
): EarlyPayoutRuleset[] {
  const root = body as EarlyPayoutRulesApiResponse | null | undefined;
  const rows = Array.isArray(root?.data) ? root.data : [];
  return rows.map(mapEarlyPayoutRuleApiToRuleset);
}

function normalizeDropdownItems<T>(body: unknown): T[] {
  const root = body as EarlyPayoutDropdownApiResponse<T> | null | undefined;
  return Array.isArray(root?.data) ? root.data : [];
}

export function mapSportsDropdownToSelectOptions(
  items: EarlyPayoutSportDropdownItem[],
): EarlyPayoutSelectOption[] {
  return items.map((item) => ({
    label: item.title,
    value: item.sport_id,
  }));
}

export function mapMarketsDropdownToSelectOptions(
  items: EarlyPayoutMarketDropdownItem[],
): EarlyPayoutSelectOption[] {
  return items.map((item) => ({
    label: formatEarlyPayoutMarketLabel(item.market_id, item.title),
    value: item.market_id,
  }));
}

export function mapRulesetsToSelectOptions(
  rulesets: EarlyPayoutRuleset[],
): EarlyPayoutSelectOption[] {
  return rulesets.map((ruleset) => ({
    label: ruleset.ruleName,
    value: ruleset.id,
  }));
}

export function mapLeaguesDropdownToSelectOptions(
  items: EarlyPayoutLeagueDropdownItem[],
): EarlyPayoutSelectOption[] {
  return items.map((item) => ({
    label: item.title,
    value: item.tournament_id,
  }));
}

export function mapEventsDropdownToSelectOptions(
  items: EarlyPayoutEventDropdownItem[],
): EarlyPayoutSelectOption[] {
  return items.map((item) => ({
    label: item.title,
    value: item.event_id,
  }));
}

export async function fetchEarlyPayoutSportsDropdown(): Promise<
  EarlyPayoutSportDropdownItem[]
> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_SPORTS_DROPDOWN);
  return normalizeDropdownItems<EarlyPayoutSportDropdownItem>(response?.data);
}

export async function fetchEarlyPayoutMarketsDropdown(): Promise<
  EarlyPayoutMarketDropdownItem[]
> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_MARKETS_DROPDOWN);
  return normalizeDropdownItems<EarlyPayoutMarketDropdownItem>(response?.data);
}

export type EarlyPayoutSelectorApi = {
  selector_id: string;
  display_name: string;
  rule_id: string;
  rule: EarlyPayoutRuleApi | null;
  selector_type: string;
  is_active: boolean;
  sport_id: string;
  sport_name: string;
  tournament_id: string | null;
  tournament_name: string | null;
  category_id: string | null;
  category_name: string | null;
  event_id: string | null;
  event_name: string | null;
  created_at: string;
  updated_at: string;
};

type EarlyPayoutSelectorsApiResponse = {
  success?: boolean;
  message?: string;
  data?: EarlyPayoutSelectorApi[];
};

function mapSelectorStatus(isActive: boolean): EarlyPayoutSelectorStatus {
  return isActive ? "Enabled" : "Disabled";
}

export function mapEarlyPayoutSelectorApiToSelector(
  selector: EarlyPayoutSelectorApi,
): EarlyPayoutSelector {
  return {
    selectorId: String(selector.selector_id ?? ""),
    selector: String(selector.selector_type ?? ""),
    details: String(selector.display_name ?? ""),
    status: mapSelectorStatus(Boolean(selector.is_active)),
    ruleId: String(selector.rule_id ?? ""),
    rule: selector.rule ? mapEarlyPayoutRuleApiToRuleset(selector.rule) : null,
  };
}

export function normalizeEarlyPayoutSelectorsResponse(
  body: unknown,
): EarlyPayoutSelector[] {
  const root = body as EarlyPayoutSelectorsApiResponse | null | undefined;
  const rows = Array.isArray(root?.data) ? root.data : [];
  return rows.map(mapEarlyPayoutSelectorApiToSelector);
}

export async function fetchEarlyPayoutSelectors(): Promise<EarlyPayoutSelector[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_SELECTORS);
  return normalizeEarlyPayoutSelectorsResponse(response?.data);
}

export type CreateEarlyPayoutSelectorSportPayload = {
  rule_id: string;
  selector_type: "sport";
  sport_id: string;
  sport_name: string;
};

export type CreateEarlyPayoutSelectorTournamentPayload = {
  rule_id: string;
  selector_type: "tournament";
  sport_id: string;
  sport_name: string;
  tournament_id: string;
  tournament_name: string;
  category_id: string | null;
  category_name: string | null;
};

export type CreateEarlyPayoutSelectorEventPayload = {
  rule_id: string;
  selector_type: "event";
  sport_id: string;
  sport_name: string;
  tournament_id: string;
  tournament_name: string;
  category_id: string | null;
  category_name: string | null;
  event_id: string;
  event_name: string;
};

export type CreateEarlyPayoutSelectorPayload =
  | CreateEarlyPayoutSelectorSportPayload
  | CreateEarlyPayoutSelectorTournamentPayload
  | CreateEarlyPayoutSelectorEventPayload;

export type UpdateEarlyPayoutSelectorPayload = {
  selectorId: string;
  is_active: boolean;
};

export async function fetchEarlyPayoutLeaguesDropdown(
  sportId: string,
): Promise<EarlyPayoutLeagueDropdownItem[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_LEAGUES_DROPDOWN, {
    params: { sport_id: sportId },
  });
  return normalizeDropdownItems<EarlyPayoutLeagueDropdownItem>(response?.data);
}

export function useEarlyPayoutLeaguesDropdown(
  sportId: string | undefined,
  enabled = true,
) {
  return useQuery<EarlyPayoutLeagueDropdownItem[], Error>({
    queryKey: [EARLY_PAYOUT_LEAGUES_DROPDOWN_QUERY_KEY, sportId],
    queryFn: () => fetchEarlyPayoutLeaguesDropdown(sportId as string),
    enabled: enabled && Boolean(sportId),
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export type EarlyPayoutEventsQueryDateRange = {
  start_date: string;
  end_date: string;
};

export async function fetchEarlyPayoutEventsDropdown(
  tournamentId: string,
  dateRange: EarlyPayoutEventsQueryDateRange = getEarlyPayoutEventsQueryDateRange(),
): Promise<EarlyPayoutEventDropdownItem[]> {
  const { start_date, end_date } = dateRange;
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_EVENTS_DROPDOWN, {
    params: {
      event_type: "match",
      tournament_id: tournamentId,
      start_date,
      end_date,
    },
  });
  return normalizeDropdownItems<EarlyPayoutEventDropdownItem>(response?.data);
}

export function useEarlyPayoutEventsDropdown(
  tournamentId: string | undefined,
  enabled = true,
) {
  const dateRange = getEarlyPayoutEventsQueryDateRange();
  const { start_date, end_date } = dateRange;

  return useQuery<EarlyPayoutEventDropdownItem[], Error>({
    queryKey: [
      EARLY_PAYOUT_EVENTS_DROPDOWN_QUERY_KEY,
      tournamentId,
      start_date,
      end_date,
    ],
    queryFn: () =>
      fetchEarlyPayoutEventsDropdown(tournamentId as string, dateRange),
    enabled: enabled && Boolean(tournamentId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export async function createEarlyPayoutSelector(
  payload: CreateEarlyPayoutSelectorPayload,
): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(
    API_ROUTES.CREATE_EARLY_PAYOUT_SELECTOR,
    payload,
  );
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useCreateEarlyPayoutSelector() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, CreateEarlyPayoutSelectorPayload>({
    mutationFn: createEarlyPayoutSelector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EARLY_PAYOUT_SELECTORS_QUERY_KEY],
      });
    },
  });
}

export async function updateEarlyPayoutSelector(
  payload: UpdateEarlyPayoutSelectorPayload,
): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.patch(
    API_ROUTES.UPDATE_EARLY_PAYOUT_SELECTOR.replace(
      "{selector_id}",
      toEarlyPayoutSelectorPathId(payload.selectorId),
    ),
    { is_active: payload.is_active },
  );
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useUpdateEarlyPayoutSelector() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, UpdateEarlyPayoutSelectorPayload>({
    mutationFn: updateEarlyPayoutSelector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EARLY_PAYOUT_SELECTORS_QUERY_KEY],
      });
    },
  });
}

export async function deleteEarlyPayoutSelector(selectorId: string): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.delete(
    API_ROUTES.DELETE_EARLY_PAYOUT_SELECTOR.replace(
      "{selector_id}",
      toEarlyPayoutSelectorPathId(selectorId),
    ),
  );
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useDeleteEarlyPayoutSelector() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, string>({
    mutationFn: deleteEarlyPayoutSelector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EARLY_PAYOUT_SELECTORS_QUERY_KEY],
      });
    },
  });
}

export async function fetchEarlyPayoutRules(): Promise<EarlyPayoutRuleset[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_EARLY_PAYOUT_RULES);
  return normalizeEarlyPayoutRulesResponse(response?.data);
}

export async function createEarlyPayoutRule(
  payload: CreateEarlyPayoutRulePayload,
): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(API_ROUTES.CREATE_EARLY_PAYOUT_RULE, payload);
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useCreateEarlyPayoutRule() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, CreateEarlyPayoutRulePayload>({
    mutationFn: createEarlyPayoutRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EARLY_PAYOUT_RULES_QUERY_KEY] });
    },
  });
}

export type UpdateEarlyPayoutRulePayload = {
  ruleId: string;
  rule_name: string;
  advantage: number;
  lines?: EarlyPayoutRuleLinePayload[];
};

export async function updateEarlyPayoutRule(
  payload: UpdateEarlyPayoutRulePayload,
): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.patch(
    API_ROUTES.UPDATE_EARLY_PAYOUT_RULE.replace("{rule_id}", payload.ruleId),
    {
      rule_name: payload.rule_name,
      advantage: payload.advantage,
      ...(payload.lines ? { lines: payload.lines } : {}),
    },
  );
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useUpdateEarlyPayoutRule() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, UpdateEarlyPayoutRulePayload>({
    mutationFn: updateEarlyPayoutRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EARLY_PAYOUT_RULES_QUERY_KEY] });
    },
  });
}

export async function deleteEarlyPayoutRule(ruleId: string): Promise<string> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.delete(
    API_ROUTES.DELETE_EARLY_PAYOUT_RULE.replace("{rule_id}", ruleId),
  );
  return extractEarlyPayoutApiMessage(response?.data);
}

export function useDeleteEarlyPayoutRule() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, string>({
    mutationFn: deleteEarlyPayoutRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EARLY_PAYOUT_RULES_QUERY_KEY] });
    },
  });
}

export function useEarlyPayoutRules() {
  return useQuery<EarlyPayoutRuleset[], Error>({
    queryKey: [EARLY_PAYOUT_RULES_QUERY_KEY],
    queryFn: fetchEarlyPayoutRules,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

export function useEarlyPayoutSportsDropdown(enabled = true) {
  return useQuery<EarlyPayoutSportDropdownItem[], Error>({
    queryKey: [EARLY_PAYOUT_SPORTS_DROPDOWN_QUERY_KEY],
    queryFn: fetchEarlyPayoutSportsDropdown,
    enabled,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export function useEarlyPayoutMarketsDropdown(enabled = true) {
  return useQuery<EarlyPayoutMarketDropdownItem[], Error>({
    queryKey: [EARLY_PAYOUT_MARKETS_DROPDOWN_QUERY_KEY],
    queryFn: fetchEarlyPayoutMarketsDropdown,
    enabled,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export function useEarlyPayoutSelectors(enabled = true) {
  return useQuery<EarlyPayoutSelector[], Error>({
    queryKey: [EARLY_PAYOUT_SELECTORS_QUERY_KEY],
    queryFn: fetchEarlyPayoutSelectors,
    enabled,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}
